import { useCallback, useEffect, useState } from "react";
import { API_BASE, type Agent, type DatasetSummary, type Model } from "./types";

/** Loads GET /api/evaluation/datasets. `reload` re-fetches (e.g. after save/import/delete). */
export function useDatasets(onError?: (msg: string) => void) {
  const [datasets, setDatasets] = useState<DatasetSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/evaluation/datasets`);
      const data = await response.json();
      setDatasets(data.datasets || []);
    } catch (err) {
      console.error("Failed to load datasets:", err);
      onError?.("Vragenlijsten konden niet worden geladen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { datasets, loading, reload };
}

/** Loads GET /api/evaluation/agents and GET /api/evaluation/models once. */
export function useAgentsAndModels(onError?: (msg: string) => void) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [loadingModels, setLoadingModels] = useState(true);

  useEffect(() => {
    (async () => {
      setLoadingAgents(true);
      try {
        const response = await fetch(`${API_BASE}/api/evaluation/agents`);
        const data = await response.json();
        setAgents(data.agents || []);
      } catch (err) {
        console.error("Failed to load agents:", err);
        onError?.("Agents konden niet worden geladen");
      } finally {
        setLoadingAgents(false);
      }
    })();
    (async () => {
      setLoadingModels(true);
      try {
        const response = await fetch(`${API_BASE}/api/evaluation/models`);
        const data = await response.json();
        setModels(data.models || []);
      } catch (err) {
        console.error("Failed to load models:", err);
        // Don't set error, just use default model
      } finally {
        setLoadingModels(false);
      }
    })();
  }, []);

  return { agents, models, loadingAgents, loadingModels };
}

/** Polls GET /api/evaluation/status every 5 s; only `is_active` + `run_id` counts as running. */
export function useEvaluationStatus() {
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [isEvaluationRunning, setIsEvaluationRunning] = useState(false);

  useEffect(() => {
    const check = async () => {
      try {
        const response = await fetch("/api/evaluation/status");
        const data = await response.json();
        if (data.is_active && data.run_id) {
          setActiveRunId(data.run_id);
          setIsEvaluationRunning(true);
        } else {
          setActiveRunId(null);
          setIsEvaluationRunning(false);
        }
      } catch (err) {
        console.error("Failed to check evaluation status:", err);
      }
    };
    check();
    const interval = setInterval(check, 5000);
    return () => clearInterval(interval);
  }, []);

  return { activeRunId, setActiveRunId, isEvaluationRunning, setIsEvaluationRunning };
}
